import { NavigationGuardNext, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import { routes } from '..';
import { useUserStoreWithOut } from '/@/store/modules/user';
import { isLocal } from '/@/libs/env';
import { NOTFOUND } from '../constant';
import { isMobile, to as toPromise } from '@flinks/utils';
import { getInviteInfoByShortLink } from '/@/api/live';
import { ResourcePlatformType } from '/@/api/live/model';
import { isMiniProgram } from '/@/hooks/web/usePlatform';

export type BeforeMiddlewareFn = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => void;

export type AfterMiddlewareFn = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: (flag?: boolean) => void
) => void;

interface WhiteHandler {
  (routes: RouteRecordRaw[]): string[];
  cache?: string[];
}

/**
 * 获取白名单路由信息
 */
const getWhiteList: WhiteHandler = (routes) => {
  if (getWhiteList.cache) return getWhiteList.cache;
  const whiteList: string[] = [];
  function handler(routes: RouteRecordRaw[]) {
    routes.forEach((item) => {
      if (!item.meta?.auth && item.name) {
        whiteList.push(item.name as string);
      }
      if (item?.children?.length) {
        handler(item.children);
      }
    });
  }
  handler(routes);
  getWhiteList.cache = whiteList;
  return whiteList;
};

/**
 * 设置标题中间件
 */
export const titleMiddleware: BeforeMiddlewareFn = (to, from, next) => {
  document.title = to.meta.title as string;
  next();
};

/**
 * 判断白名单中间件
 */
export const whiteListMiddleware: BeforeMiddlewareFn = (to, from, next) => {
  const whiteList = getWhiteList(routes);

  // 如果是白名单内的路由直接通行
  if (to.name && whiteList.includes(to.name as string)) {
    return next(false);
  }
  next();
};

/**
 * 分享邀请链接进入
 */
export const inviteLinkMiddleware: BeforeMiddlewareFn = async (to, from, next) => {
  const { param } = to.query;
  if (param) {
    const userStore = useUserStoreWithOut();
    const [, res] = await toPromise(getInviteInfoByShortLink({ shortLink: param as string }));
    if (res) {
      userStore.setInviteLinkInfo(res);
    }
  }
  next();
};

export const platformMiddleware: BeforeMiddlewareFn = (to, from, next) => {
  const userStore = useUserStoreWithOut();
  const inviteLinkInfo = userStore.getInviteLinkInfo;
  if ((isMobile() && !isLocal() && inviteLinkInfo) || (inviteLinkInfo && isMiniProgram())) {
    const search = location.search;
    if (inviteLinkInfo.resourceInviteBaseInfoVO.resourcePlatform === ResourcePlatformType.BJY) {
      // 百家云
      location.replace(`${location.origin}/h5/mall/middle${search}`);
    } else {
      // 灵狮
      location.replace(`${location.origin}/links-h5/middle${search}`);
    }
  } else {
    next();
  }
};

let isCompanyError = false;
/**
 * 获取企业id中间件
 */
export const companyIdMiddleware: BeforeMiddlewareFn = async (to, from, next) => {
  if (to.name === NOTFOUND || isCompanyError) {
    return next(false);
  }
  const userStore = useUserStoreWithOut();
  if (isLocal()) {
    userStore.setCompanyId('879673588276264960');
    userStore.getWebConfigByCompanyId();
  }
  if (userStore.getCompanyId) return next();
  if (userStore.getCompanyId) return next();
  try {
    await userStore.getCompanyIdAction(document.domain);
    await userStore.getWebConfigByCompanyId();
    next();
  } catch (err) {
    isCompanyError = true;
    next({ name: NOTFOUND });
  }
};

/**
 * 判断登录授权中间件
 */
export const authMiddleware: BeforeMiddlewareFn = async (to, from, next) => {
  const userStore = useUserStoreWithOut();
  const { token, ...rest } = to.query;
  if (token) {
    userStore.setToken(token as string);
    await userStore.getUserInfoAction();
    return next({ path: to.path, query: rest });
  }
  if (userStore.getToken) {
    if (!userStore.getUserInfo || !Object.keys(userStore.getUserInfo).length) {
      await userStore.getUserInfoAction();
    }
    next();
  } else {
    next();
  }
};
